import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContractLedger } from '@wsphere/warranties/domain';
import { firstValueFrom } from 'rxjs';

function toDollars(amount: number) {
  return amount / 100;
}

@Injectable()
export class LedgerStatementService {
  constructor(private http: HttpClient) {}

  async retrieve(distributorId: string): Promise<ContractLedger.RetrievePartyLedgerStatementQueryResponse> {
    const statement = await firstValueFrom(
      this.http.get<ContractLedger.RetrievePartyLedgerStatementQueryResponse>(
        `/api/distributors/${distributorId}/ledger-statement`
      )
    );

    // Same cents convention as the rest of the ledger (see ContractLedgerWebService) — persisted
    // amounts match warranty.retail/Payment.amount, not the dollars the currency doc describes.
    // The per-contract list is fetched separately (see the ledger-statement page's
    // vs-connected-table, bound directly to /ledger-statement/contracts) — a party can have far
    // more contract activity than is worth fetching just for this summary.
    return {
      ...statement,
      totals: statement.totals.map(total => ({
        ...total,
        netAllocation: toDollars(total.netAllocation),
        netDisbursement: toDollars(total.netDisbursement),
        outstandingPayable: toDollars(total.outstandingPayable),
      })),
    };
  }

  async recordPayout(
    warrantyId: string,
    body: Omit<ContractLedger.RecordSellerPayoutRequest, 'warrantyId' | 'amount'> & { amount: number }
  ): Promise<ContractLedger.RecordSellerPayoutResponse> {
    // Dollars in from the dialog's currency-masked input, cents out to the API — same convention
    // retrieve() reverses for display. NOT Math.round()'d to a whole cent: the dialog accepts up
    // to 4 decimal places so a prorated/split payout can be recorded exactly (see
    // LEDGER_AMOUNT_DECIMAL_PRECISION) — roundToLedgerPrecision only cleans up IEEE 754 floating
    // point noise from the multiplication, it never discards a real fractional-cent digit.
    return await firstValueFrom(
      this.http.post<ContractLedger.RecordSellerPayoutResponse>(`/api/warranties/${warrantyId}/seller-payout`, {
        ...body,
        amount: ContractLedger.roundToLedgerPrecision(body.amount * 100),
      })
    );
  }
}
